import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  Divider,
  Snackbar,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Popper,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
  Badge,
  Fade,
  Fab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SortIcon from '@mui/icons-material/Sort';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { LoadingButton } from '@mui/lab';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Header } from 'components/Header';
import { BulkStatusUpdateRequest, ServiceRequestDetail, ServiceRequestSummary } from '../services/types';
import type { UpdateServiceRequest } from '../services/api';
import { getServiceRequestByDeliveryId, getServiceRequestsByOrderId, getServiceRequestsByStatusAndStoreId, searchServiceRequests, updateStatus, StatusIds, getFilteredStatusCount, bulkStatusUpdate } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { CUSTOMER_NAME_PLACEHOLDER,FILTER_STORAGE_KEY } from '../services/const';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { Scanner as QrScannerComp } from '@yudiel/react-qr-scanner';
import { CONSOLIDATED_STATUS_OPTIONS, consolidatedKeyToCsv, getConsolidatedStatusLabel, ORDER_TYPE_OPTIONS, OrderType, ALL_STATUS_IDS_CSV, SORT_OPTIONS, ORDER_AGE_OPTIONS, SortBy, SORT_MAPPING, OrderAge, getScrollState, clearScrollState, saveScrollState } from 'services/util';
import {
  deriveHandoverTimestamp,
  extractAssociateInfo,
  formatOrderDateTime,
  parseServiceList
} from 'utils/orderMeta';
import QuickView from 'components/QuickView/QuickView';
import { QuickViewResponse } from 'components/QuickView/type';

interface StoreViewLandingPageProps {
  externalIdentity: string;
  storeId: string;
  associateId: string;
  associateEmail: string;
  associateName?: string;
  profileId?: string;
}

type LocationFilter = 'all' | 'Store' | 'Kerrville' | 'Kerrville,Store';

type ScanType = 'order' | 'delivery' | 'general';

interface NormalizedScanResult {
  original: string;
  normalized: string;
  type: ScanType;
  queries: string[];
}

const normalizeScanInput = (raw: string): NormalizedScanResult | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const collapsed = trimmed.replace(/\s+/g, '');
  let normalized = collapsed;
  let type: ScanType = 'general';

  const explicitMatch = collapsed.match(/<open_logistic_order>(.*?)<\/open_logistic_order>/i) ||
    collapsed.match(/<open_logistic_order><([^>]+)>/i);
  if (explicitMatch && explicitMatch[1]) {
    normalized = explicitMatch[1].replace(/[<>]/g, '').trim();
    type = 'order';
  } else if (/open_logistic_order/i.test(collapsed)) {
    const inlineMatch = collapsed.match(/open_logistic_order([^<>\s]+)/i);
    if (inlineMatch && inlineMatch[1]) {
      normalized = inlineMatch[1].replace(/[^a-z0-9]/gi, '');
      type = 'order';
    }
  }

  if (/^[A-Za-z]{2}\d{4,}$/.test(normalized) || /^RS\d+/i.test(normalized) || /^SO\d+/i.test(normalized)) {
    type = 'order';
  } else if (/^\d+$/.test(normalized)) {
    type = 'delivery';
  }

  const upper = /[a-z]/i.test(normalized) ? normalized.toUpperCase() : normalized;
  normalized = upper;

  const queries = new Set<string>();
  queries.add(normalized);
  if (normalized !== collapsed) queries.add(collapsed);
  if (normalized !== trimmed) queries.add(trimmed);

  return {
    original: trimmed,
    normalized,
    type,
    queries: Array.from(queries).filter(Boolean),
  };
};

const dedupeSummaries = (items: ServiceRequestSummary[]): ServiceRequestSummary[] => {
  const map = new Map<string, ServiceRequestSummary>();
  items.forEach((item, idx) => {
    if (!item) return;
    const serviceItemId = ((item as any)?.serviceItemId || '').toString();
    const key = serviceItemId || item.orderId?.toString?.() || item.orderExternalId || `idx-${idx}`;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

const detailToSummary = (detail: ServiceRequestDetail): ServiceRequestSummary => {
  const location = ((detail as any)?.itemLocation || (detail as any)?.locationType || '') as string;
  const statusId = (detail as any)?.statusId as number | string | undefined;
  return {
    serviceItemId: detail.serviceItemId,
    orderId: detail.orderId,
    orderExternalId: detail.orderExternalId,
    customerEmail: detail.customerEmail,
    customerName: detail.customerName,
    sku: detail.sku,
    tag: detail.tag || '',
    metal: detail.metal,
    productType: detail.productType,
    measuredSize: detail.measuredSize,
    locationType: location,
    statusId,
    orderType: detail.orderType,
    created: detail.created,
    serviceUpdated: detail.serviceUpdated,
    formattedServiceUpdated: detail.formattedServiceUpdated,
    open: detail.open,
    associateId: detail.associateId,
    associateEmail: detail.associateEmail,
    associateName: detail.associateName,
    closeByAssociateId: detail.closeByAssociateId,
    closeByAssociateEmail: detail.closeByAssociateEmail,
    closeByAssociateName: detail.closeByAssociateName,
  };
};

const HANDOVER_STATUS = StatusIds.COMPLETED;
const READY_FOR_HANDOVER_STATUS = StatusIds.READY_TO_PICKUP;

type HandoverState = 'idle' | 'loading' | 'success' | 'error';

const buildBulkHandoverPayload = (
  items: ServiceRequestSummary[],
  associateId: string,
  associateEmail: string,
  associateName?: string
): BulkStatusUpdateRequest => {
  const serviceItems = items
    .filter((item) => item.serviceItemId)
    .map((item) => ({
      serviceItemId: item.serviceItemId as string,
      serviceGuids: parseServiceList(item.services)
        .map((svc: any) => svc?.service_guid)
        .filter(Boolean),
    }));

  return {
    status: HANDOVER_STATUS,
    associateId,
    associateName: associateName?.trim() || associateEmail || associateId,
    associateEmail,
    serviceItems,
  };
};
const getServiceItemId = (item: ServiceRequestSummary): string => {
  return ((item as any)?.serviceItemId || '').toString();
};

const getOrderKey = (item: ServiceRequestSummary): string => {
  const serviceItemId = getServiceItemId(item);
  const orderId = (item as any)?.orderId;
  return item.orderExternalId || (orderId ? String(orderId) : serviceItemId);
};

const isReadyForHandover = (item: ServiceRequestSummary): boolean => {
  const raw = (item as any)?.statusId;
  const numeric = Number(raw);
  return !Number.isNaN(numeric) && numeric === READY_FOR_HANDOVER_STATUS;
};

const extractServices = (detail: ServiceRequestDetail): any[] => {
  const raw = (detail as any)?.services;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const buildHandoverUpdate = (
  detail: ServiceRequestDetail,
  associateId: string,
  associateEmail: string,
  associateDisplayName?: string
): UpdateServiceRequest => {
  const services = extractServices(detail)
    .map((svc: any) => svc?.service_guid)
    .filter(Boolean)
    .map((guid: string) => ({ service_guid: guid, status: HANDOVER_STATUS }));

  const base: UpdateServiceRequest = {
    serviceItemId: detail.serviceItemId,
    status: HANDOVER_STATUS,
    associateId,
    associateName: associateDisplayName?.trim() || associateEmail || associateId,
    associateEmail,
  };

  return services.length ? { ...base, service: services } : base;
};

const PAGE_SIZE = 10;


interface SavedFilters {
  statusKey: string | 'all';
  locationFilter: LocationFilter;
  orderType: OrderType;
  orderAge: OrderAge;
  searchText: string;
  sortBy: SortBy;
}

export const disableScrollRestore = () => {
  saveScrollState({
    restoreScroll: false,
    loadedCount: PAGE_SIZE,
    scrollY: 0,
    page:0
  });
};
export const DEFAULT_SORT: SortBy = 'CREATED_NEWEST';
const StoreViewLandingPage: React.FC<StoreViewLandingPageProps> = ({ externalIdentity, storeId, associateId, associateEmail, associateName, profileId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false); // for "load more" UX
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ServiceRequestSummary[] | null>(null);
  const [didSearch, setDidSearch] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef<HTMLDivElement | null>(null); // for IntersectionObserver
  const findScannerContainerRef = useRef<HTMLDivElement | null>(null);

  const isSecureContext =
    typeof window !== 'undefined' &&
    (window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname));

  const empty = useMemo(() => (summary?.length ?? 0) === 0, [summary]);
  const [findDialogOpen, setFindDialogOpen] = useState(false);
  const [findInput, setFindInput] = useState('');
  const [findResults, setFindResults] = useState<ServiceRequestSummary[]>([]);
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [findScanning, setFindScanning] = useState(true);
  const [findScannerKey, setFindScannerKey] = useState(0);
  const [findCameraHint, setFindCameraHint] = useState<string | null>(null);
  const [lastScanSignature, setLastScanSignature] = useState<string | null>(null);
  const [handoverState, setHandoverState] = useState<Record<string, HandoverState>>({});
  const [handoverErrorMessages, setHandoverErrorMessages] = useState<Record<string, string>>({});
  const [orderBulkState, setOrderBulkState] = useState<Record<string, HandoverState>>({});
  const [bulkAllLoading, setBulkAllLoading] = useState(false);
  const [bubbleCounts, setBubbleCounts] = useState<QuickViewResponse | null>(null);
  const [bubbleCountsLoading, setBubbleCountsLoading] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  // Guard against stale async responses updating state
  const requestSeqRef = useRef(0);
  const lastApiErrorMessageRef = useRef<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [showBackToTop, setShowBackToTop] = useState(false);

    useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 1200);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

    const handleBackToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

  // Filter retain feature-getting session data 
  const savedFilters:SavedFilters = (() => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();
  const [searchText, setSearchText] = useState(savedFilters?.searchText ?? '');

  const [locationFilter, setLocationFilter] = useState<LocationFilter>(
    savedFilters?.locationFilter ?? 'all'
  );

  const [statusKey, setStatusKey] = useState<string | 'all'>(
    savedFilters?.statusKey ?? 'all'
  );

  const [orderType, setOrderType] = useState<OrderType>(
    savedFilters?.orderType ?? 'all'
  );
    const [orderAge, setOrderAge] = useState<OrderAge>(
    savedFilters?.orderAge ?? 'all'
  );

  const [sortBy, setSortBy] = useState<SortBy>(
    savedFilters?.sortBy ?? DEFAULT_SORT
  );

  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          statusKey,
          locationFilter,
          orderType,
          orderAge,
          searchText,
          sortBy
        })
      );
    } catch (err) {
      console.warn('Unable to persist filters', err);
    }
  }, [statusKey, locationFilter, orderType, orderAge, searchText, sortBy]);

  useEffect(() => {
      if (!summary?.length) return;

      const scrollState = getScrollState();

      if (!scrollState?.restoreScroll) return;

      requestAnimationFrame(() => {
          window.scrollTo({
              top: scrollState.scrollY,
              behavior: 'auto',
          });

          clearScrollState();

      });

  }, [summary]);


  const isProfileMode = Boolean(profileId);
  const resolvedAssociateName = useMemo(
    () => (associateName?.trim() || associateEmail || associateId),
    [associateId, associateEmail, associateName]
  );

  // const statusCsv = useMemo(() => {
  //   if (statusKey === 'all') return undefined;
  //   const csv = consolidatedKeyToCsv(statusKey);
  //   return csv || undefined;
  // }, [statusKey]);

  const effectiveQuery = useMemo(() => {
    if (isProfileMode) {
      return (profileId ?? '').trim();
    }
    return searchText.trim();
  }, [isProfileMode, profileId, searchText]);

  const groupedFindResults = useMemo(() => {
    if (!findResults.length) return [] as { key: string; orderExternalId: string; items: ServiceRequestSummary[] }[];
    const map = new Map<string, ServiceRequestSummary[]>();
    findResults.forEach((item) => {
      if (!item) return;
      const key = getOrderKey(item) || `row-${map.size}`;
      const existing = map.get(key);
      if (existing) existing.push(item);
      else map.set(key, [item]);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      key,
      orderExternalId: items[0]?.orderExternalId || key,
      items,
    }));
  }, [findResults]);

  const readyResultItems = useMemo(() => findResults.filter(isReadyForHandover), [findResults]);

  const performHandoverForItem = useCallback(async (item: ServiceRequestSummary) => {
    const serviceItemId = getServiceItemId(item);
    if (!serviceItemId) return false;
    setHandoverState((prev) => ({ ...prev, [serviceItemId]: 'loading' }));
    setHandoverErrorMessages((prev) => {
      if (!prev[serviceItemId]) return prev;
      const next = { ...prev };
      delete next[serviceItemId];
      return next;
    });
    try {
      const detail = await getServiceRequestsByOrderId(externalIdentity, serviceItemId);
      const request = buildHandoverUpdate(detail, associateId, associateEmail, resolvedAssociateName);
      await updateStatus(externalIdentity, request);
      setFindResults((prev) =>
        prev.map((entry) => {
          const id = getServiceItemId(entry);
          if (id === serviceItemId) {
            return { ...entry, statusId: HANDOVER_STATUS };
          }
          return entry;
        })
      );
      setHandoverState((prev) => ({ ...prev, [serviceItemId]: 'success' }));
      setHandoverErrorMessages((prev) => {
        if (!prev[serviceItemId]) return prev;
        const next = { ...prev };
        delete next[serviceItemId];
        return next;
      });
      return true;
    } catch (err) {
      logApiError('Marking item as collected', err);
      const message = formatApiErrorMessage(err, 'Unable to mark item as collected. Please try again.');
      lastApiErrorMessageRef.current = message;
      setHandoverState((prev) => ({ ...prev, [serviceItemId]: 'error' }));
      setHandoverErrorMessages((prev) => ({ ...prev, [serviceItemId]: message }));
      setSnack(message);
      return false;
    }
  }, [associateId, associateEmail, resolvedAssociateName, externalIdentity, setSnack]);

  const markItemCollected = useCallback((item: ServiceRequestSummary) => {
    if (!isReadyForHandover(item)) return;
    void performHandoverForItem(item);
  }, [performHandoverForItem]);

  const handleOrderBulkHandover = useCallback(async (orderKey: string, items: ServiceRequestSummary[]) => {
    const readyItems = items.filter(isReadyForHandover);
    if (!readyItems.length) {
      setSnack('No ready items to hand over.');
      return;
    }
    setOrderBulkState((prev) => ({ ...prev, [orderKey]: 'loading' }));
    lastApiErrorMessageRef.current = null;
    let anySuccess = false;
    for (const item of readyItems) {
      const success = await performHandoverForItem(item);
      anySuccess = anySuccess || success;
    }
    setOrderBulkState((prev) => ({ ...prev, [orderKey]: anySuccess ? 'success' : 'error' }));
    if (anySuccess) {
      setSnack('Ready items marked as collected.');
    } else {
      setSnack(lastApiErrorMessageRef.current ?? 'Unable to mark items as collected. Please try again.');
    }
  }, [performHandoverForItem, setSnack]);

  const handleBulkAllHandover = useCallback(async () => {
    if (!readyResultItems.length) {
      setSnack('No ready items to hand over.');
      return;
    }

    const affectedKeys = new Set<string>();

    readyResultItems.forEach((item) => {
      const key = getOrderKey(item);
      if (key) {
        affectedKeys.add(key);
      }
    });

    setOrderBulkState((prev) => {
      if (affectedKeys.size === 0) return prev;

      const next = { ...prev };

      affectedKeys.forEach((key) => {
        next[key] = 'loading';
      });

      return next;
    });

    setBulkAllLoading(true);
    lastApiErrorMessageRef.current = null;

    const payload = buildBulkHandoverPayload(
      readyResultItems,
      associateId,
      associateEmail,
      resolvedAssociateName
    );

    try {
      const response = await bulkStatusUpdate(externalIdentity, payload);

      const resultsMap = new Map(
        response?.results?.map((result) => [result.serviceItemId, result])
      );

      const successKeys = new Set<string>();
      const failedKeys = new Set<string>();

      setFindResults((prev) =>
        prev.map((item: any) => {
          const result = resultsMap.get(item.serviceItemId);

          if (!result) {
            return item;
          }

          const key = getOrderKey(item);

          if (result.status === 'FAILED') {
            if (key) {
              failedKeys.add(key);
            }

            return item;
          }

          if (key) {
            successKeys.add(key);
          }

          return {
            ...item,
            statusId: HANDOVER_STATUS,
          };
        })
      );

      setOrderBulkState((prev) => {
        if (affectedKeys.size === 0) return prev;

        const next = { ...prev };

        affectedKeys.forEach((key) => {
          if (failedKeys.has(key)) {
            next[key] = 'error';
          } else if (successKeys.has(key)) {
            next[key] = 'success';
          }
        });

        return next;
      });

      const succeeded =
        response?.results?.filter((result) => result.status !== 'FAILED')
          .length ?? 0;

      const failed =
        response?.results?.filter((result) => result.status === 'FAILED')
          .length ?? 0;

      if (failed > 0) {
        setSnack(`${succeeded} handovers completed, ${failed} failed.`);
      } else {
        setSnack('Ready items marked as collected.');
      }
    } catch (err) {
      const message = formatApiErrorMessage(
        err,
        'Unable to mark items as collected. Please try again.'
      );

      lastApiErrorMessageRef.current = message;

      setOrderBulkState((prev) => {
        if (affectedKeys.size === 0) return prev;

        const next = { ...prev };

        affectedKeys.forEach((key) => {
          next[key] = 'error';
        });

        return next;
      });

      setSnack(message);
    } finally {
      setBulkAllLoading(false);
    }
  }, [
    readyResultItems,
    associateId,
    associateEmail,
    resolvedAssociateName,
    externalIdentity,
    setSnack,
  ]);

  const handleOpenFindDialog = useCallback(() => {
    setFindDialogOpen(true);
    setFindInput('');
    setFindResults([]);
    setFindError(null);
    setFindCameraHint(null);
    setLastScanSignature(null);
    setHandoverState({});
    setHandoverErrorMessages({});
    setOrderBulkState({});
    setBulkAllLoading(false);
    if (isSecureContext) {
      setFindScanning(true);
      setFindScannerKey((k) => k + 1);
    } else {
      setFindScanning(false);
    }
  }, [isSecureContext]);

  const handleCloseFindDialog = useCallback(async() => {
    setFindDialogOpen(false);
    setFindInput('');
    setFindResults([]);
    setFindError(null);
    setFindCameraHint(null);
    setLastScanSignature(null);
    setHandoverState({});
    setHandoverErrorMessages({});
    setOrderBulkState({});
    setBulkAllLoading(false);
    setFindScanning(isSecureContext);
    // Reload listing with current filters/sort
    await fetchPage(0, true, {
      statusKey,
      locationFilter,
      orderType,
      orderAge,
      query: searchText,
      sortBy,
    });
  }, [isSecureContext,statusKey,locationFilter,orderType,orderAge,searchText,sortBy]);

  const runFindLookup = useCallback(async (target: NormalizedScanResult, origin: 'scan' | 'manual') => {
    setFindLoading(true);
    setFindError(null);
    setFindResults([]);
    try {
      const aggregated: ServiceRequestSummary[] = [];
      const querySet = new Set<string>(target.queries.map((q) => q.trim()).filter(Boolean));

      if (target.type === 'delivery') {
        try {
          const detail = await getServiceRequestByDeliveryId(externalIdentity, target.normalized);
          if (detail) {
            const summary = detailToSummary(detail);
            aggregated.push(summary);
            if (summary.orderExternalId) {
              querySet.add(summary.orderExternalId);
            }
          }
        } catch (err: any) {
          const status = err?.response?.status as number | undefined;
          if (status && status !== 404) throw err;
        }
      }

      if (!querySet.size) {
        querySet.add(target.normalized);
      }

      for (const q of querySet) {
        const trimmed = q.trim();
        if (!trimmed) continue;
        const matches = await searchServiceRequests(
          externalIdentity,
          ALL_STATUS_IDS_CSV,
          storeId,
          trimmed,
          undefined,
          undefined,
          "100"
        );
        if (Array.isArray(matches)) {
          aggregated.push(...matches);
        }
      }

      const deduped = dedupeSummaries(aggregated);
      setHandoverState({});
      setHandoverErrorMessages({});
      setOrderBulkState({});
      setBulkAllLoading(false);
      setFindResults(deduped);

      if (origin === 'scan' && deduped.length === 1) {
        const [onlyMatch] = deduped;
        handleCloseFindDialog();
        if (onlyMatch) {
          navigate('/order-details', { state: onlyMatch });
        }
        return;
      }

      if (!deduped.length) {
        setFindError('No matching orders found.');
      }
    } catch (err) {
      logApiError('Find Order lookup failed', err);
      setFindResults([]);
      setFindError(formatApiErrorMessage(err, 'Unable to locate an order with that value.'));
    } finally {
      setFindLoading(false);
    }
  }, [externalIdentity, storeId, handleCloseFindDialog, navigate]);

  const fetchPage = useCallback(
    async (pageToFetch: number, hardReload = false, overrides?: {
      statusKey?: string | 'all';
      locationFilter?: LocationFilter;
      orderType?: OrderType;
      orderAge?:OrderAge;
      query?: string;            // manual text
      sortBy?: SortBy;
      profileMode?: boolean;
      limit?:number;
    }) => {
      const isFirstPage = pageToFetch === 0;

      const statusKeyToUse = overrides?.statusKey ?? statusKey;
      const locationToUse = overrides?.locationFilter ?? locationFilter;
      const typeToUse = overrides?.orderType ?? orderType;
      const ageToUse = overrides?.orderAge ?? orderAge;

      const profileMode = overrides?.profileMode ?? isProfileMode;

      const locationParam = locationToUse === 'all' ? undefined : locationToUse;
      const typeParam = typeToUse === 'all' ? undefined : typeToUse;
      const ageParam = ageToUse === 'all' ? undefined : ageToUse;
      const sortByToUse = overrides?.sortBy ?? sortBy;
      const sortConfig = SORT_MAPPING[sortByToUse];
      const qSource = overrides?.query ?? (profileMode ? profileId ?? '' : searchText);
      const qText = (qSource ?? '').trim();
      const hasTextSearch = !profileMode && qText.length > 0;

      const mustUseSearch = profileMode || qText.length > 0;
      if (pageToFetch === 0 && didSearch && !hardReload && mustUseSearch) {
        return;
      }
      const mySeq = (hardReload || isFirstPage) ? ++requestSeqRef.current : requestSeqRef.current;

      const statusCsvToUse = (() => {
        if (statusKeyToUse === 'all') {
          const quickTypeSelected = typeToUse === 'QUICK';
          if (quickTypeSelected) return ALL_STATUS_IDS_CSV;
          if (profileMode) return ALL_STATUS_IDS_CSV;
          if (mustUseSearch && hasTextSearch) return ALL_STATUS_IDS_CSV;
          return undefined;
        }
        return consolidatedKeyToCsv(statusKeyToUse) || undefined;
      })();

      if (isFirstPage) {
        setSummary([]);
        setLoading(true);
      } else {
        setPageLoading(true);
      }

      try {
        if (mustUseSearch) {
          const limit = overrides?.limit ?? PAGE_SIZE;
          const offset = pageToFetch * limit;
          const searchLimit = String(limit);
          const searchOffset = String(offset);
          
          const results = await searchServiceRequests(
            externalIdentity,
            statusCsvToUse,
            storeId,
            qText,
            locationParam,
            typeParam,
            searchLimit,
            searchOffset,
            ageParam,
            sortConfig.sortBy,
            sortConfig.sortOrder
          );
          if (requestSeqRef.current !== mySeq) return;
          if (isFirstPage) {
            setSummary(results);
          } else {
            setSummary(prev =>
              requestSeqRef.current === mySeq
                ? [...(prev || []), ...results]
                : prev
            );
          }
          setHasMore(results.length === limit);
          setPage(pageToFetch);

        } else {
          const limit = overrides?.limit ?? PAGE_SIZE;
          const offset = pageToFetch * limit;
          const summaries = await getServiceRequestsByStatusAndStoreId(
            externalIdentity,
            statusCsvToUse,
            storeId,
            limit,
            offset,
            locationParam,
            typeParam,
            ageParam,
            sortConfig.sortBy,
            sortConfig.sortOrder
          );
          if (requestSeqRef.current !== mySeq) return;
          if (isFirstPage) setSummary(summaries);
          else setSummary(prev => (requestSeqRef.current === mySeq ? [...(prev || []), ...summaries] : prev));
          setHasMore(summaries.length === limit);
          const restoredScroll = getScrollState();

          if (
              pageToFetch === 0 &&
              overrides?.limit &&
              restoredScroll?.restoreScroll
          ) {
              setPage(restoredScroll.page);
          } else {
              setPage(pageToFetch);
          }
        }
      } catch (err) {
        logApiError('Loading service requests', err);
        if (requestSeqRef.current !== mySeq) return;
        setError(formatApiErrorMessage(err, 'Failed to load service requests'));
      } finally {
        if (isFirstPage) setLoading(false);
        else setPageLoading(false);
      }
    }, [didSearch, externalIdentity, storeId, statusKey, locationFilter, orderType,orderAge,sortBy, isProfileMode, profileId, searchText]);

  const handleFindSubmit = useCallback(() => {
    const value = findInput.trim();
    if (!value) {
      setFindError('Enter an order number, tag, or delivery ID.');
      return;
    }
    const normalized = normalizeScanInput(value) || {
      original: value,
      normalized: /[a-z]/i.test(value) ? value.toUpperCase() : value,
      type: /^\d+$/.test(value) ? 'delivery' as ScanType : 'general',
      queries: [value],
    };
    setFindCameraHint(null);
    setFindScanning(false);
    setLastScanSignature(null);
    runFindLookup(normalized, 'manual');
  }, [findInput, runFindLookup]);

  const handleFindClear = useCallback(() => {
    setFindInput('');
    setFindResults([]);
    setFindError(null);
    setLastScanSignature(null);
    setHandoverState({});
    setHandoverErrorMessages({});
    setOrderBulkState({});
    setBulkAllLoading(false);
    if (isSecureContext) {
      setFindScanning(true);
      setFindScannerKey((k) => k + 1);
    }
  }, [isSecureContext]);

  const handleScanResult = useCallback((raw: string) => {
    const normalized = normalizeScanInput(raw);
    if (!normalized) return;
    const signature = `${normalized.type}:${normalized.normalized}`;
    if (signature === lastScanSignature) return;
    setLastScanSignature(signature);
    setFindInput(normalized.normalized);
    setFindCameraHint(null);
    setFindScanning(false);
    runFindLookup(normalized, 'scan');
  }, [lastScanSignature, runFindLookup]);

  useEffect(() => {
    if (!findDialogOpen || !findScanning) return;
    const node = findScannerContainerRef.current;
    if (!node) return;
    const video: HTMLVideoElement | null = node.querySelector('video');
    if (!video) return;

    const handleEnded = () => {
      setFindCameraHint('Camera closed. Tap Scan Again to continue.');
      setFindScanning(false);
    };

    video.addEventListener('pause', handleEnded);
    video.addEventListener('ended', handleEnded);
    (video as any).addEventListener?.('webkitendfullscreen', handleEnded);

    return () => {
      video.removeEventListener('pause', handleEnded);
      video.removeEventListener('ended', handleEnded);
      (video as any).removeEventListener?.('webkitendfullscreen', handleEnded);
    };
  }, [findDialogOpen, findScanning]);


  const doSearch = useCallback(async () => {
    const q = effectiveQuery;

    if (!q || loading || pageLoading) return;

    setError(null);
    setDidSearch(true);

    await fetchPage(0, true, {
      query: q,
    });
  }, [effectiveQuery, loading, pageLoading, fetchPage]);
  const handleClear = useCallback(async () => {
    setError(null);
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch (err) {
      console.warn('Unable to clear saved filters', err);
    }
    const nextLoc: LocationFilter = 'all';
    const nextStatus: string | 'all' = 'all';
    const nextType: OrderType | 'all' = 'all';
    const nextAge: OrderAge | 'all' = 'all';
    setLocationFilter(nextLoc);
    setStatusKey(nextStatus);
    setOrderType(nextType);
    setOrderAge(nextAge)
    setSortBy(DEFAULT_SORT);
    if (isProfileMode) {
      setDidSearch(true);
      await fetchPage(0, true, { statusKey: nextStatus, locationFilter: nextLoc, orderType: nextType,orderAge: nextAge,sortBy:DEFAULT_SORT, query: '' });
    } else {
      setSearchText('');
      setDidSearch(false);
      await fetchPage(0, true, { statusKey: nextStatus, locationFilter: nextLoc, orderType: nextType,orderAge: nextAge,sortBy:DEFAULT_SORT, query: '' });
    }
    await fetchBubbleCounts()
  }, [fetchPage, isProfileMode]);

  const fetchBubbleCounts = async () => {
    try {
      setBubbleCountsLoading(true);

      const response = await getFilteredStatusCount(externalIdentity, {
        storeId,
        filters: [
          {
            statusId: 1,
            locationType: 'Kerrville_all',
          },
          {
            statusId: 8,
            locationType: null,
          },
        ],
      });

      setBubbleCounts(response);
    } catch (error) {
      console.error('Failed to fetch bubble counts', error);
    } finally {
      setBubbleCountsLoading(false);
    }
  };

  // initial + refetch only when identity/store changes
  // avoid depending on fetchPage/status/location here to prevent
  // unintended resets after a text search
  useEffect(() => {
    if (!externalIdentity) {
      setError('Missing authentication context');
      return;
    }
    setPage(0);
    // In profile mode, consider ourselves "searched" and fetch via search
    setDidSearch(isProfileMode);    
    const scrollState = getScrollState();
    fetchPage(0, true, {
    statusKey,
    locationFilter,
    orderType,
    orderAge,
    query: searchText,
    sortBy,
    limit:
      scrollState?.restoreScroll && scrollState.loadedCount > 0
        ? scrollState.loadedCount
        : PAGE_SIZE,
  });
    fetchBubbleCounts()
  }, [externalIdentity, storeId, isProfileMode]);

  // IntersectionObserver for auto "Load More"
  useEffect(() => {
    if (!hasMore || pageLoading || loading) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          fetchPage(page + 1);
        }
      },
      { rootMargin: '600px 0px' } // prefetch a bit earlier
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, page, pageLoading]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      doSearch();
    }
  };

  const EmptyState = () => (
    <Box
      sx={{
        px: 3,
        py: 6,
        textAlign: 'center',
        color: 'text.secondary',
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {didSearch ? 'No matches found' : 'No service requests'}
      </Typography>
      <Typography variant="body2">
        {didSearch
          ? 'Try a different term or search by another field.'
          : 'New service/collection orders will show up here as they arrive.'}
      </Typography>
    </Box>
  );

  const LoadingSkeleton = () => (
    <Box sx={{ px: 2, py: 2 }}>
      {Array.from({ length: 3 }).map((_, idx) => (
        <Card key={idx} sx={{ mb: 2, borderRadius: 2 }}>
          <Skeleton variant="rectangular" height={36} />
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Card>
      ))}
    </Box>
  );
  const shipToProductCareCount =
    bubbleCounts?.statusCounts.find(
      ({ status, locationType }) =>
        status === 1 && locationType === 'Kerrville_all'
    )?.count ?? 0;

  const receiveGuestOrderCount =
    bubbleCounts?.statusCounts.find(({ status }) => status === 8)?.count ?? 0;
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
            <Typography variant="h6">Service & Special Orders</Typography>
            {/* {!!summary?.length && (
              <Chip size="small" label={summary.length} sx={{ borderRadius: 1 }} />
            )} */}
            <QuickView storeId={storeId} token={externalIdentity}/>
          </Box>
        }
        sticky
        elevateOnScroll
        align="left"
      />

      {/* top search + filter area */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          mb: 1.5,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.primary.light, 0.06),
          // subtle glassy feel in light/dark
          backdropFilter: 'saturate(120%) blur(4px)',
        })}
      >
        <Stack spacing={1.25}>
          {!isProfileMode && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems="stretch">
              <Button
                variant="contained"
                startIcon={<QrCodeScannerIcon />}
                onClick={handleOpenFindDialog}
                sx={(theme) => ({
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: 52,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                  '&:hover': { boxShadow: '0 8px 18px rgba(0,0,0,0.12)' },
                  backgroundImage:
                    theme.palette.mode === 'light'
                      ? 'linear-gradient(135deg, rgba(51,102,255,0.12), rgba(51,102,255,0.65))'
                      : 'linear-gradient(135deg, rgba(51,102,255,0.25), rgba(51,102,255,0.65))',
                })}
              >
                Find Order
              </Button>
              <Badge
                badgeContent={shipToProductCareCount} 
                invisible={bubbleCountsLoading || shipToProductCareCount === 0}
                max={99}
                overlap="rectangular"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#C32C61',
                      color: '#ffffff',
                      marginRight:"8px"
                    },
                  }}
              >
              <Button
                variant="outlined"
                startIcon={<LocalShippingOutlinedIcon />}
                onClick={() => navigate('/bulk-tracking')}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: 52,
                  width:"100%"
                }}
              >
                Ship to Product Care
              </Button>
              </Badge>
              <Badge
                badgeContent={receiveGuestOrderCount}
                max={99}
                invisible={bubbleCountsLoading || receiveGuestOrderCount === 0}
                overlap="rectangular"
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#C32C61',
                      color: '#ffffff',
                      marginRight:"8px"

                    },
                  }}
              >
              <Button
                variant="outlined"
                startIcon={<ShoppingBagOutlinedIcon />}
                onClick={() => navigate('/scan-delivery')}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: 52,
                }}
              >
                Receive Guest Order
              </Button>
              </Badge> 
              <Button
                variant="outlined"
                startIcon={<DoneAllIcon />}
                onClick={() => navigate('/bulk-finish-service')}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: 52,
                }}
              >
                Bulk Finish Service
              </Button>
            </Stack>
          )}

          {!isProfileMode && (
            <TextField
              variant="outlined"
              label={`Search`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyPress}
              size="small"
              disabled={loading || pageLoading}
              sx={(theme) => ({
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl
              variant="outlined"
              size="small"
              sx={(theme) => ({
                width: '25%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
            >
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select<string | 'all'>
                labelId="status-filter-label"
                value={statusKey}
                label="Status"
                onChange={(e) => {
                  const val = e.target.value as string | 'all';
                  setStatusKey(val);
                  setDidSearch(false);
                  fetchPage(0, true, { statusKey: val });
                }}
                renderValue={(value) => (value === 'all' ? 'All' : (CONSOLIDATED_STATUS_OPTIONS.find((s) => s.key === value)?.label ?? '—'))}
                disabled={loading || pageLoading}
              >
                <MenuItem value={'all'}>All</MenuItem>
                {CONSOLIDATED_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              sx={(theme) => ({
                width: '25%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
            >
              <InputLabel id="location-filter-label">Location</InputLabel>
              <Select<LocationFilter>
                labelId="location-filter-label"
                value={locationFilter}
                label="Location"
                onChange={(e) => {
                  const nextLoc = e.target.value as LocationFilter;
                  setLocationFilter(nextLoc);
                  setDidSearch(false);
                  fetchPage(0, true, { locationFilter: nextLoc });
                }}
                renderValue={(value) => {
                  if (value === 'all') return 'All';
                  if (value === 'Store') return 'In Store Only';
                  if (value === 'Kerrville') return 'Product Care Only';
                  return 'Product Care And In Store';
                }}
                disabled={loading || pageLoading}
              >
                <MenuItem value={'all'}>All</MenuItem>
                <MenuItem value="Store">In Store Only</MenuItem>
                <MenuItem value="Kerrville">Product Care Only</MenuItem>
                <MenuItem value="Kerrville,Store">Product Care And In Store</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              sx={(theme) => ({
                width: '25%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
            >
              <InputLabel id="order-type-label">
              {/* iPhone (xs) */}
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Type
              </Box>

              {/* iPad and larger (sm+) */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Order Type
              </Box> 
              </InputLabel>
              <Select<OrderType>
                labelId="order-type-label"
                value={orderType}
                label={isMobile ? "Type": "Order Type"}
                onChange={(e) => {
                  const nextType = e.target.value as OrderType;
                  setOrderType(nextType);
                  // Selecting an order type switches to search mode (non-paginated)
                  setDidSearch(true);
                  fetchPage(0, true, { orderType: nextType });
                }}
                disabled={loading || pageLoading}
              >
                {ORDER_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            
            <FormControl
              variant="outlined"
              size="small"
              sx={(theme) => ({
                width: '25%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  transition: 'box-shadow .2s ease',
                  '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                  '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                },
              })}
            >
              <InputLabel id="order-age-label">
              {/* iPhone (xs) */}
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Age
              </Box>

              {/* iPad and larger (sm+) */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Order Age
              </Box> 
              </InputLabel>
              <Select<OrderAge>
                labelId="order-age-label"
                value={orderAge}
                label={isMobile ? 'Age' : 'Order Age'}
                onChange={(e) => {
                  const nextAge = e.target.value as OrderAge;
                  setOrderAge(nextAge);
                  // Selecting an order age switches to search mode (non-paginated)
                  setDidSearch(true);
                  fetchPage(0, true, { orderAge: nextAge });
                }}
                disabled={loading || pageLoading}
              >
                {ORDER_AGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
          </Stack>
        </Stack>

        {/* Row 2: Buttons */}
        {!isProfileMode && (
          <Stack direction="row" spacing={1} mt={1.5}>
            <Button
              variant="contained"
              onClick={doSearch}
              disabled={!searchText.trim() || loading || pageLoading}
              sx={(theme) => ({
                flex:2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                '&.Mui-disabled': {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                },
              })}
            >
              Search
            </Button>
            <ClickAwayListener onClickAway={() => setSortMenuOpen(false)}>
            <Box
              ref={sortMenuRef}
              sx={{
                position: 'relative',
                flex: 1,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setSortMenuOpen((prev) => !prev)}
                disabled={loading || pageLoading}
                startIcon={<SortIcon />}
                endIcon={
                  sortMenuOpen ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
               {/* Shows only on mobile (xs) screens */}
              <Box component="span" sx={{ display: { xs: 'block', sm: 'none' } }}>
                Sort
              </Box>

              {/* Shows on tablet (sm) screens and up (iPad and bigger) */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Sort By
              </Box> 
              </Button>

              <Popper
                open={sortMenuOpen}
                anchorEl={sortMenuRef.current}
                placement="bottom-start"
                sx={{
                  zIndex: 9999,
                  mt: "8px !important",
                }}
              >
                  <Paper
                    elevation={8}
                    sx={{
                      width:{xs:250, sm:300},
                      p: 1,
                      borderRadius: 2,
                    }}
                  >
                    <RadioGroup
                      value={sortBy}
                      onChange={(e) => {
                        const value = e.target.value as SortBy;

                        setSortBy(value);
                        setSortMenuOpen(false);

                        fetchPage(0, true, {
                          statusKey,
                          locationFilter,
                          orderType,
                          orderAge,
                          query: searchText,
                          sortBy: value,
                        });
                      }}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          label={option.label}
                          control={
                            <Radio
                              sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                  color: 'black',
                                },
                              }}
                            />
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Paper>
              </Popper>
            </Box>
            </ClickAwayListener>

            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={loading || pageLoading}
              sx={{flex:1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Clear
            </Button>
          </Stack>
        )}
      </Paper>

      {/* show top loading bar when fetching first page */}
      {loading && (
        <Box sx={{ px: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* error banner (inline, non-blocking) */}
      {error && (
        <Box p={2}>
          <Alert severity="warning" variant="outlined">
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        </Box>
      )}

      {/* skeletons during initial load */}
      {loading && !summary && <LoadingSkeleton />}

      {/* data / empty state */}
      {!loading && summary && empty && <EmptyState />}

      {!loading && summary && !empty && (
        <Box sx={{ pt: 1, px: { xs: 2, sm: 3, lg: 6 }, pb: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, md: 3 },
              gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(360px, 1fr))' },
              justifyItems: 'center',
            }}
          >
            {summary?.map((s, i) => {
              const customerName =
                (s.customerName ?? '').toString().trim() || CUSTOMER_NAME_PLACEHOLDER;
              const associateInfo = extractAssociateInfo(s as Record<string, any>);
              const associateLabel = (() => {
                if (associateInfo.name) {
                  return associateInfo.id ? `${associateInfo.name} (${associateInfo.id})` : associateInfo.name;
                }
                if (associateInfo.id) return associateInfo.id;
                if (associateInfo.email) return associateInfo.email;
                return null;
              })();
              const handoverAssociateInfo = extractAssociateInfo({
                associateName: (s as any)?.closeByAssociateName,
                associateId: (s as any)?.closeByAssociateId,
                associateEmail: (s as any)?.closeByAssociateEmail,
              });
              const handoverAssociateLabel = (() => {
                if (handoverAssociateInfo.name) {
                  return handoverAssociateInfo.id
                    ? `${handoverAssociateInfo.name} (${handoverAssociateInfo.id})`
                    : handoverAssociateInfo.name;
                }
                if (handoverAssociateInfo.id) return handoverAssociateInfo.id;
                if (handoverAssociateInfo.email) return handoverAssociateInfo.email;
                return null;
              })();
              const serviceEntries = parseServiceList((s as any)?.services);
              const cardHandoverTimestamp = deriveHandoverTimestamp(s as Record<string, any>, serviceEntries);
              const cardHandoverLabel = cardHandoverTimestamp
                ? formatOrderDateTime(cardHandoverTimestamp)
                : null;
              return (
                <Card
                  key={`${(s as any).serviceItemId || s.orderExternalId || i}`}
                  sx={(theme) => ({
                    width: '100%',
                    maxWidth: 520,
                    borderRadius: 3,
                    overflow: 'hidden',
                    position: 'relative',
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                    backgroundImage:
                      theme.palette.mode === 'light'
                        ? 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,1))'
                        : 'linear-gradient(180deg, rgba(22,22,22,0.9), rgba(22,22,22,1))',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&:active': { transform: 'translateY(0px)' },
                  })}
                >
                  <CardActionArea
                    onClick={() => {
                        saveScrollState({
                            restoreScroll: true,
                            scrollY: window.scrollY,
                            loadedCount: summary?.length ?? PAGE_SIZE,
                            page
                        });

                        navigate('/order-details', { state: s });
                    }}
                >
                    {/* Header */}
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 2,
                        py: 1.25,
                        background:
                          theme.palette.mode === 'light'
                            ? alpha(theme.palette.primary.main, 0.06)
                            : alpha(theme.palette.primary.main, 0.18),
                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                      })}
                    >
                      <Avatar
                        variant="rounded"
                        sx={(theme) => ({
                          width: 48,
                          height: 48,
                          borderRadius: 1.2,
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          color: theme.palette.primary.main,
                        })}
                      >
                        <Inventory2OutlinedIcon fontSize="small" />
                      </Avatar>
                      <Stack spacing={0.25} sx={{ minWidth: 0, height: 48, justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Order&nbsp;ID:&nbsp;{s.orderExternalId}
                        </Typography>
                        {(() => {
                          const rawStatusId = (s as any).statusId as string | number | undefined;
                          const label = getConsolidatedStatusLabel(rawStatusId);
                          const lower = label.toLowerCase();
                          const tone = (theme: any) => {
                            if (lower.includes('cancel')) return { bg: theme.palette.error.main, fg: theme.palette.error.main };
                            if (lower.includes('complete')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
                            if (lower.includes('ready')) return { bg: theme.palette.success.main, fg: theme.palette.success.main };
                            if (lower.includes('hold')) return { bg: theme.palette.warning.main, fg: theme.palette.warning.main };
                            if (lower.includes('open')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                            if (lower.includes('progress')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                            if (lower.includes('kerrville')) return { bg: theme.palette.info.main, fg: theme.palette.info.main };
                            return { bg: theme.palette.primary.main, fg: theme.palette.primary.main };
                          };

                          return (
                            <Chip
                              size="small"
                              label={label}
                              sx={(theme) => ({
                                borderRadius: 1,
                                fontWeight: 600,
                                bgcolor: alpha(tone(theme).bg, 0.15),
                                color: tone(theme).fg,
                              })}
                            />
                          );
                        })()}
                      </Stack>
                    </Box>
                    <CardContent sx={{ py: 1.25 }}>
                      {/* Type + Location chips */}
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        {(() => {
                          const pt = String(
                            (s as any).orderType || ''
                          ).toUpperCase();

                          const meta = {
                            QUICK: {
                              label: 'Quick Service',
                              icon: <BoltOutlinedIcon />,
                              colorKey: (t: any) => t.palette.warning.main,
                            },
                            SPECIAL_ORDER: {
                              label: 'Special Order',
                              icon: <WorkspacePremiumOutlinedIcon />,
                              colorKey: (t: any) => t.palette.secondary.main,
                            },
                            STANDARD: {
                              label: 'Standard Service',
                              icon: <BuildCircleOutlinedIcon />,
                              colorKey: (t: any) => t.palette.primary.main,
                            },
                            BOPIS: {
                              label: 'BOPIS-BOSS',
                              icon: <ShoppingBagOutlinedIcon />,
                              colorKey: (t: any) => t.palette.success.main,
                            },
                          } as const;

                          const key = (pt in meta ? pt : 'STANDARD') as keyof typeof meta;
                          const def = meta[key];

                          return (
                            <Chip
                              size="small"
                              icon={def.icon}
                              label={def.label}
                              sx={(theme) => {
                                const color = def.colorKey(theme);
                                return {
                                  borderRadius: 2,
                                  bgcolor: alpha(color, 0.12),
                                  color,
                                  '& .MuiChip-icon': { color },
                                };
                              }}
                            />
                          );
                        })()}
                        {(() => {
                          const pt = String(
                            (s as any).orderType ||
                            (s as any).order_type || ''
                          ).toUpperCase();
                          const loc = (s.locationType || '').toString();
                          const label = (() => {
                            if (pt === 'BOPIS') return 'Store';
                            if (loc.includes('Kerrville') && loc.includes('Store')) return 'Kerrville and Store';
                            if (loc) return loc;
                            return pt === 'SPECIAL_ORDER' ? 'Kerrville' : 'Store';
                          })();
                          const displayLabel = label
                            .replace(/Kerrville/g, 'Product Care')
                            .replace(/Store/g, 'In Store');
                          const icon = label === 'Store' ? <StorefrontOutlinedIcon /> : <PlaceOutlinedIcon />;
                          return (
                            <Chip
                              size="small"
                              icon={icon}
                              label={displayLabel}
                              sx={(theme) => {
                                let color = theme.palette.grey[600];
                                if (label === 'Store') color = theme.palette.primary.main;
                                else if (label === 'Kerrville') color = theme.palette.info.main;
                                else if (label === 'Kerrville and Store') color = theme.palette.success.main;
                                return {
                                  borderRadius: 2,
                                  bgcolor: alpha(color, 0.12),
                                  color,
                                  '& .MuiChip-icon': { color },
                                };
                              }}
                            />
                          );
                        })()}
                      </Stack>
                      <Stack divider={<Divider flexItem />} spacing={1.25}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">SKU</Typography>
                          <Typography variant="body2" fontWeight={600}>{s.sku}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Tag</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(() => {
                              const t = (s as any)?.tag ?? '';
                              const str = String(t);
                              return str.length > 30 ? `${str.slice(0, 30)}...` : str;
                            })()}
                          </Typography>
                        </Stack>


                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Customer</Typography>
                          <Typography variant="body2">{customerName}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Email</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>{s.customerEmail}</Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Associate</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>
                            {associateLabel ?? '—'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Handover Associate</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'right' }}>
                            {handoverAssociateLabel ?? '—'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Created</Typography>
                          <Typography variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                            {formatOrderDateTime(s.created)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Picked Up</Typography>
                          <Typography variant="body2">{cardHandoverLabel ?? '—'}</Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
            <Fade in={showBackToTop}>
              <Fab
                size="medium"
                onClick={handleBackToTop}
                sx={(theme) => ({
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                  zIndex: theme.zIndex.fab,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
                  '&:hover': {
                  bgcolor: theme.palette.background.paper,
                  },
                  '&:active': {
                    bgcolor: theme.palette.background.paper,
                  },
                  '&.Mui-focusVisible': {
                    bgcolor: theme.palette.background.paper,
                  },
                  })}
              >
                <KeyboardArrowUpIcon />
              </Fab>
            </Fade>
          </Box>

          {/* load more area */}
          {hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={() => fetchPage(page + 1)}
                disabled={pageLoading}
              >
                {pageLoading ? (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <span>Loading…</span>
                  </Stack>
                ) : (
                  'Load More'
                )}
              </Button>
            </Box>
          )}

          {/* invisible sentinel for auto-load-on-scroll */}
          {hasMore && <Box ref={sentinelRef} sx={{ height: 1 }} />}
        </Box>
      )}

      <Dialog
        open={findDialogOpen}
        onClose={handleCloseFindDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Find Order</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {isSecureContext ? (
              findScanning ? (
                <Box ref={findScannerContainerRef} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <QrScannerComp
                    key={findScannerKey}
                    onScan={(codes: any[]) => {
                      const text = Array.isArray(codes) && codes[0]?.rawValue
                        ? codes[0].rawValue
                        : (codes?.[0]?.rawValue || codes?.[0]?.rawValueText || '');
                      if (!text) return;
                      handleScanResult(text);
                      setFindScanning(false);
                    }}
                    onError={(err: any) => {
                      console.error(err);
                      setFindCameraHint('Camera error. Tap Scan Again to retry.');
                      setFindScanning(false);
                    }}
                    constraints={{ facingMode: 'environment' }}
                    styles={{ video: { width: '100%' } }}
                    formats={['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'pdf417']}
                  />
                </Box>
              ) : (
                <Stack spacing={1} alignItems="stretch">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFindScanning(true);
                      setFindScannerKey((k) => k + 1);
                      setFindCameraHint(null);
                      setLastScanSignature(null);
                    }}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    Scan Again
                  </Button>
                  {findCameraHint && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {findCameraHint}
                    </Typography>
                  )}
                </Stack>
              )
            ) : (
              <Alert severity="info" variant="outlined">
                Camera access requires HTTPS or localhost. Use manual entry below.
              </Alert>
            )}

            <Divider textAlign="center">or</Divider>

            <TextField
              label="Order / Tag / Delivery ID"
              value={findInput}
              onChange={(e) => setFindInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFindSubmit();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: findInput ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear">
                      <IconButton size="small" onClick={handleFindClear}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleFindSubmit}
                disabled={!findInput.trim() || findLoading}
                sx={(theme) => ({
                  flex: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&.Mui-disabled': {
                    color: theme.palette.text.primary,
                    opacity: 0.6,
                  },
                })}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleFindClear}
                disabled={findLoading && !findResults.length && !findInput}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Reset
              </Button>
            </Stack>

            {findLoading && <LinearProgress />}

            {findError && (
              <Alert severity="warning" variant="outlined">
                {findError}
              </Alert>
            )}

            {!findLoading && !findError && !findResults.length && (
              <Typography variant="body2" color="text.secondary">
                Scan or search to view order matches.
              </Typography>
            )}

            {!findLoading && groupedFindResults.length > 0 && (
              <Stack spacing={1.5}>
                {groupedFindResults.map(({ key, orderExternalId, items }) => {
                  const eligibleItemIds = items
                    .filter(isReadyForHandover)
                    .map((item) => getServiceItemId(item))
                    .filter(Boolean);
                  const orderStatus = orderBulkState[key] || 'idle';
                  const orderBulkLoading = orderStatus === 'loading';
                  const orderBulkSuccess = orderStatus === 'success';
                  const readyCount = eligibleItemIds.length;
                  return (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={(theme) => ({
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.divider, 0.75),
                        backgroundColor: alpha(theme.palette.background.paper, 0.7),
                      })}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="subtitle1" fontWeight={700}>
                            Order {orderExternalId || '—'}
                          </Typography>
                          <Chip
                            label={`${items.length} item${items.length > 1 ? 's' : ''}`}
                            size="small"
                            sx={(theme) => ({
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            })}
                          />
                        </Stack>

                        <Stack spacing={0.75}>
                          {items.map((item, idx) => {
                            const serviceItemId = getServiceItemId(item);
                            const itemKey = serviceItemId ? `${serviceItemId}-${idx}` : `${key}-${idx}`;
                            const numericStatus = Number((item as any)?.statusId);
                            const eligible = isReadyForHandover(item);
                            const state = serviceItemId ? handoverState[serviceItemId] : undefined;
                            const loading = state === 'loading';
                            const success = state === 'success' || numericStatus === HANDOVER_STATUS;
                            const errorState = state === 'error';
                            const errorMessage = serviceItemId ? handoverErrorMessages[serviceItemId] : undefined;
                            const statusLabel = getConsolidatedStatusLabel((item as any).statusId);
                            return (
                              <Paper
                                key={itemKey}
                                variant="outlined"
                                sx={(theme) => ({
                                  p: 1,
                                  borderRadius: 2,
                                  borderColor: alpha(theme.palette.primary.main, 0.12),
                                  backgroundColor: alpha(theme.palette.primary.light, 0.06),
                                })}
                              >
                                <Stack spacing={0.75}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack spacing={0.25}>
                                      <Typography variant="body2" fontWeight={600}>
                                        SKU {item.sku || '—'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Tag {item.tag || '—'}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="body2" fontWeight={600}>
                                      {statusLabel}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                    {eligible ? (
                                      // <Stack direction="row" spacing={1} justifyContent="flex-end">
                                      <LoadingButton
                                        size="small"
                                        variant={success ? 'contained' : 'outlined'}
                                        color={success ? 'success' : 'primary'}
                                        onClick={() => markItemCollected(item)}
                                        loading={loading}
                                        disabled={loading || success || !serviceItemId}
                                        // sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}

                                      >
                                        {success ? 'Handed Over' : 'Confirm Handover'}
                                      </LoadingButton>

                                      // </Stack>

                                    ) : success ? (
                                      <Typography variant="caption" color="success.main">
                                        Collected
                                      </Typography>
                                    ) : errorState ? (
                                      <Typography variant="caption" color="error.main">
                                        {errorMessage || 'Failed to mark collected. Try again.'}
                                      </Typography>
                                    ) : null}

                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => {
                                        handleCloseFindDialog();
                                        navigate('/order-details', { state: item });
                                      }}
                                      sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                                    >
                                      View Details
                                    </Button>
                                  </Stack>

                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>

                        <Stack direction="row" spacing={1} justifyContent="flex-end">

                          {(readyCount > 0 || orderBulkSuccess) && (
                            <LoadingButton
                              size="small"
                              variant={orderBulkSuccess ? 'contained' : 'outlined'}
                              color={orderBulkSuccess ? 'success' : 'primary'}
                              onClick={() => handleOrderBulkHandover(key, items)}
                              loading={orderBulkLoading}
                              disabled={orderBulkLoading || (readyCount === 0 && !orderBulkSuccess)}
                              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
                            >
                              {orderBulkSuccess
                                ? 'Handed Over'
                                : `Confirm Handover of ${readyCount} Item${readyCount === 1 ? '' : 's'}`}
                            </LoadingButton>
                          )}
                          {orderStatus === 'error' && (
                            <Typography variant="caption" color="error.main">
                              Bulk handover failed. Try again.
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {readyResultItems.length > 0 && (
              <LoadingButton
                variant="contained"
                color="secondary"
                onClick={handleBulkAllHandover}
                loading={bulkAllLoading}
                disabled={bulkAllLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {bulkAllLoading
                  ? 'Confirming handovers...'
                  : `Confirm Handover of All ${readyResultItems.length} Ready Items`}
              </LoadingButton>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFindDialog} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>      

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default StoreViewLandingPage;
